import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@roger/ui-components';
import { GripVertical, X } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../store';
import { updateAgencyConfig } from '../services/agencyConfig';
import {
  INTAKE_MANAGER_FULL_CATALOG,
  effectiveIntakeManagerColumns,
  labelForIntakeManagerColumn,
} from '../config/intakeManagerColumns';
import { useToast } from '../hooks/useToastContext';

const MAX_COLUMNS = 25;

type SortableRowProps = {
  columnId: string;
  onRemove: () => void;
};

const SortableSelectedRow: React.FC<SortableRowProps> = ({
  columnId,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: columnId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className='flex items-center gap-2 rounded-md border bg-background px-2 py-1.5'
    >
      <button
        type='button'
        className='cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-muted'
        {...attributes}
        {...listeners}
        aria-label='Reorder'
      >
        <GripVertical className='h-4 w-4' />
      </button>
      <span className='flex-1 text-sm truncate'>
        {labelForIntakeManagerColumn(columnId)}
      </span>
      <Button
        type='button'
        variant='ghost'
        size='icon'
        className='h-8 w-8 shrink-0'
        onClick={onRemove}
        aria-label='Remove column'
      >
        <X className='h-4 w-4' />
      </Button>
    </div>
  );
};

export type IntakeManagerColumnsEditorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** From `/agency-config`; null = product default; undefined before first load */
  storedColumnIds: string[] | null | undefined;
  onAfterSave: () => void | Promise<void>;
};

const IntakeManagerColumnsEditorDialog: React.FC<
  IntakeManagerColumnsEditorDialogProps
> = ({ open, onOpenChange, storedColumnIds, onAfterSave }) => {
  const { user } = useStore(
    useShallow(state => ({
      user: state.user,
    }))
  );
  const { error: showError, success: showSuccess } = useToast();

  const [draftOrder, setDraftOrder] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const agencyId = user?.agency?.id;

  useEffect(() => {
    if (open && user?.agency) {
      setDraftOrder(effectiveIntakeManagerColumns(storedColumnIds));
      setSearch('');
    }
  }, [open, user?.agency, storedColumnIds]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const availableCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    return INTAKE_MANAGER_FULL_CATALOG.filter(meta => {
      if (draftOrder.includes(meta.id)) return false;
      if (!q) return true;
      return meta.label.toLowerCase().includes(q);
    }).sort((a, b) => a.label.localeCompare(b.label));
  }, [draftOrder, search]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = draftOrder.indexOf(String(active.id));
    const newIndex = draftOrder.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    setDraftOrder(arrayMove(draftOrder, oldIndex, newIndex));
  };

  const addColumn = (id: string) => {
    if (draftOrder.length >= MAX_COLUMNS) {
      showError(`You can add at most ${MAX_COLUMNS} columns.`);
      return;
    }
    if (draftOrder.includes(id)) return;
    setDraftOrder(prev => [...prev, id]);
  };

  const removeColumn = (id: string) => {
    setDraftOrder(prev => prev.filter(x => x !== id));
  };

  const handleSave = async () => {
    if (!agencyId) {
      showError('No agency in session.');
      return;
    }
    setSaving(true);
    try {
      await updateAgencyConfig({ intake_manager_columns: draftOrder });
      showSuccess('Intake Manager columns saved.');
      await onAfterSave();
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      showError('Failed to save columns');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!agencyId) {
      showError('No agency in session.');
      return;
    }
    setSaving(true);
    try {
      await updateAgencyConfig({ intake_manager_columns: null });
      showSuccess('Columns reset to default.');
      await onAfterSave();
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      showError('Failed to reset columns');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[90vh] flex flex-col'>
        <DialogHeader>
          <DialogTitle>Intake Manager columns</DialogTitle>
        </DialogHeader>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 min-h-0'>
          <div className='flex flex-col min-h-0 border rounded-md'>
            <div className='p-3 border-b space-y-2'>
              <Label htmlFor='col-search'>Add columns</Label>
              <Input
                id='col-search'
                placeholder='Search…'
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className='h-64 md:h-80 overflow-y-auto'>
              <ul className='p-2 space-y-1'>
                {availableCatalog.map(meta => (
                  <li
                    key={meta.id}
                    className='flex items-center gap-2 rounded-sm px-2 py-1 hover:bg-muted/60'
                  >
                    <span className='text-sm flex-1'>{meta.label}</span>
                    <Button
                      type='button'
                      variant='secondary'
                      size='sm'
                      disabled={draftOrder.length >= MAX_COLUMNS}
                      onClick={() => addColumn(meta.id)}
                    >
                      Add
                    </Button>
                  </li>
                ))}
                {availableCatalog.length === 0 && (
                  <li className='text-sm text-muted-foreground px-2 py-4 text-center'>
                    No matching columns to add.
                  </li>
                )}
              </ul>
            </div>
          </div>
          <div className='flex flex-col min-h-0 border rounded-md'>
            <div className='p-3 border-b'>
              <Label>Column order ({draftOrder.length})</Label>
              <p className='text-xs text-muted-foreground mt-1'>
                Drag to reorder. This layout applies to everyone at your agency.
              </p>
            </div>
            <div className='h-64 md:h-80 overflow-y-auto'>
              <div className='p-2 space-y-2'>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={draftOrder}
                    strategy={verticalListSortingStrategy}
                  >
                    {draftOrder.map(id => (
                      <SortableSelectedRow
                        key={id}
                        columnId={id}
                        onRemove={() => removeColumn(id)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className='gap-2 sm:gap-0'>
          <Button
            type='button'
            variant='outline'
            onClick={handleReset}
            disabled={saving}
          >
            Reset to default
          </Button>
          <Button
            type='button'
            variant='secondary'
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type='button' onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IntakeManagerColumnsEditorDialog;
